import type { ReportDB } from '@creator/sdk/modules/report/report.model';
import ReadMore from '@creator/ui/components/ReadMore/ReadMore';
import Spinner from '@creator/ui/components/Spinner/Spinner';
import Text from '@creator/ui/components/Text/Text';
import UserDisplayName from '@creator/ui/components/UserDisplayName/UserDisplayName';
import { convertFirebaseTimestampToDate, toCustomLocaleDate } from '@creator/ui/utils/date-utils';
import MenuItem from '@src/basic-components/MenuItem/MenuItem';
import MenuPopover from '@src/basic-components/MenuPopover/MenuPopover';
import Td from '@src/basic-components/Table/Td/Td';
import Tr from '@src/basic-components/Table/Tr/Tr';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { getMyUser, isUserModerator } from '@src/model/user/helpers';
import { useReportActions } from '@src/store/reports';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReportRowProps {
    report: ReportDB;
    index: number;
    tokenName: string;
    selectedUser: string;
    revealedReports: string[];
    onSelectUser: (username: string) => void;
    onSelectPostId: (postId: string, title: string, commentId?: string) => void;
    onModeratorRevealUserClick: (encryptedUser: string, reportId: string, tokenName: string) => Promise<void>;
}

const ReportRow: FC<ReportRowProps> = ({ report, index, tokenName, selectedUser, revealedReports, onSelectUser, onSelectPostId, onModeratorRevealUserClick }) => {
    const { t } = useTranslation();
    const { updateReportStatus } = useReportActions();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState<'Done' | 'Rejected' | null>(null);

    const myUser = getMyUser();
    const isModerator = myUser ? isUserModerator(myUser.id, tokenName) : false;

    async function onUpdateReportStatus(reportId: string, status: 'Rejected' | 'Done'): Promise<void> {
        if (!myUser) return;
        setUpdatingStatus(status);
        try {
            await updateReportStatus({ newStatus: status, reportId: reportId, tokenName: tokenName, userId: myUser.id });
            setUpdatingStatus(null);
            setIsMenuOpen(false);
        } catch (e) {
            setUpdatingStatus(null);
        }
    }

    function renderTime() {
        return (
            <Td className="text-left">
                <Text>{toCustomLocaleDate(convertFirebaseTimestampToDate(report.createdAt))}</Text>
            </Td>
        );
    }

    function renderStatus() {
        const status = report.status;
        let className = '';
        if (status === 'New') className = 'text-primary-500 dark:text-primary-400';
        if (status === 'Rejected') className = 'text-red';
        if (status === 'Done') className = 'text-green2 dark:text-green';

        const trigger = (
            <Text className={className} onClick={() => setIsMenuOpen(true)}>
                {status}
            </Text>
        );

        if (!isModerator) {
            return <Td className="text-center">{trigger}</Td>;
        }

        const menuItems = [
            <MenuItem key="done" onClick={() => onUpdateReportStatus(report.id, 'Done')}>
                <Text>Done</Text>
                {updatingStatus === 'Done' && <Spinner className="ml-1" />}
            </MenuItem>,
            <MenuItem key="rejected" onClick={() => onUpdateReportStatus(report.id, 'Rejected')}>
                <Text>Rejected</Text>
                {updatingStatus === 'Rejected' && <Spinner className="ml-1" />}
            </MenuItem>,
        ];

        return (
            <Td className="text-center">
                <MenuPopover
                    className="cursor-pointer"
                    popoverProps={{
                        isVisible: isMenuOpen,
                    }}
                    onClickOutside={() => setIsMenuOpen(false)}
                    renderTrigger={() => trigger}
                >
                    {menuItems}
                </MenuPopover>
            </Td>
        );
    }

    function renderType() {
        return (
            <Td className="">
                <Text>{t(`tokenPage.reports.columns.${report.type.toLowerCase()}`)}</Text>
            </Td>
        );
    }

    function renderReporter() {
        const reporterName = report.reporterName || 'Anonymous';
        const reporterId = report.reporterId;

        const displayName = reporterName.slice(0, 10);
        const isRevealed = revealedReports.includes(report.id);

        return (
            <Td className="">
                <div
                    className={'flex items-center cursor-pointer'}
                    onClick={() => (isRevealed ? onSelectUser(reporterName) : onModeratorRevealUserClick(reporterId, report.id, tokenName))}
                >
                    {isRevealed && <ProfileImage tokenName={tokenName} displayName={reporterName} className="w-6 h-6 mr-2 shrink-0" />}
                    <UserDisplayName displayName={displayName} textProps={{ className: `${isModerator ? 'hover:text-primary-500' : ''} font-bold` }} />
                </div>
            </Td>
        );
    }

    function renderReported() {
        const reportedName = report.userName;

        return (
            <Td className="">
                <div className="flex items-center cursor-pointer" onClick={() => onSelectUser(reportedName)}>
                    <ProfileImage tokenName={tokenName} displayName={reportedName} className="w-6 h-6 mr-2 shrink-0" />
                    <UserDisplayName displayName={reportedName} textProps={{ className: 'hover:text-primary-500 font-bold' }} />
                </div>
            </Td>
        );
    }

    function renderPostComment() {
        let title = '';
        if (report.type === 'Comment' || report.type === 'Post') {
            title = report.postTitle;
        }

        return (
            <Td className="">
                <div className="cursor-pointer" onClick={() => onSelectPostId(report.postId, title, report.type === 'Comment' ? report.commentId : '')}>
                    <Text enableMultiline={true} className="block max-w-md overflow-hidden font-bold hover:text-primary-500">
                        {title}
                    </Text>
                </div>
            </Td>
        );
    }

    function renderReason() {
        const text = report.reportText || report.reportReason || ' ';
        return (
            <Td>
                <ReadMore lineClamp={2} className="max-w-md">
                    <Text enableMultiline={true}>{text}</Text>
                </ReadMore>
            </Td>
        );
    }

    return (
        <Tr>
            {renderTime()}
            {renderStatus()}
            {renderType()}
            {renderReporter()}
            {renderReported()}
            {renderPostComment()}
            {renderReason()}
        </Tr>
    );
};

export default ReportRow;
